import { merge, random } from 'lodash';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { LocationType, TLocation, getRandomBoss, getRandomLocation } from '../locations/location.generators';
import { TNamedBookSlice, createBookSlice, getBookInitialState } from './book-store.slice';
import { TNamedEnemySlice, createEnemySlice, getEnemyInitialState } from './enemy-store.slice';
import { TNamedGameLogSlice, createGameLogSlice, getGameLogInitialState } from './game-log-store.slice';
import { HeroAttackType, TNamedHeroSlice, createHeroSlice, getHeroInitialState } from './hero-store.slice';
import { TNamedLocationSlice, createLocationSlice, getLocationInitialState } from './location-store.slice';
import { TNamedShopSlice, createShopSlice, getShopInitialState } from './shop-store.slice';
import { createOptionsSlice, getOptionsInitialState, TNamedOptionsSlice } from "./options-store.slice";

type TAppState = { _hasHydrated: boolean } & TNamedGameLogSlice & TGameActions & TNamedHeroSlice & TNamedLocationSlice & TNamedShopSlice & TNamedEnemySlice & TNamedBookSlice & TNamedOptionsSlice;

type TGameActions = {
   gameHasStarted: boolean;
   gameActions: {
      navigateToLocation(params: { location: TLocation }): void;
      navigateToRandomLocation(): void;
      siggAttackEnemy(params: { attackType: HeroAttackType; enemyId: string }): void;
      siggBashesEnemy(enemyId: string): void;
      enemyAttackSigg(params: { byEnemyId: string }): void;
      allEnemiesAttackSigg(): void;
      startNewRun(): void;
      setGameHasStarted(): void;
      resetState(): void;
   };
};

export const useGameStore = create<TAppState>()(
   devtools(
      persist(
         immer((set, get, store) => ({
            _hasHydrated: false,
            options: createOptionsSlice(set, get, store),
            log: createGameLogSlice(set, get, store),
            hero: createHeroSlice(set, get, store),
            location: createLocationSlice(set, get, store),
            shop: createShopSlice(set, get, store),
            enemy: createEnemySlice(set, get, store),
            book: createBookSlice(set, get, store),
            gameHasStarted: false,
            gameActions: {
               /**
                * Navigates to the specified location and performs necessary actions.
                * @param params - The parameters for navigating to the location.
                * @param params.location - The location to navigate to.
                */
               navigateToLocation(params: { location: TLocation }) {
                  const enemyActions = get().enemy.actions;
                  const locationActions = get().location.actions;
                  const daysGone = get().location.state.daysVentured;

                  // set the active locations
                  locationActions.setActiveLocation({ location: params.location });

                  // generate the enemies for the given location
                  switch (params.location.locationType) {
                     case LocationType.GobboAttack:
                        enemyActions.generateRandomGobbos(params.location.baddieQuantity);
                        break;
                     case LocationType.BanditAttack:
                        enemyActions.generateRandomBandits(params.location.baddieQuantity);
                        break;
                     case LocationType.BossAttack:
                        if (params.location.name === 'Intimidating Bone Pile') {
                           enemyActions.generateUgonBoss();
                        }
                        if (params.location.name === 'Ransacked Village') {
                           enemyActions.generateBaronBoss();
                        }
                        if (params.location.name === 'Toxic Mire') {
                           enemyActions.generateBeastBoss();
                        }
                        break;

                     default:
                        enemyActions.setEnemies({ enemies: [] });
                  }
                  locationActions.setDaysVentured(daysGone + 1);

                  get().hero.actions.heroicActions.resetActionPoints();
               },

               /**
                * Navigates to a random location. trigger a boss infrequently every 18th level
                */
               navigateToRandomLocation() {
                  let location = getRandomLocation();
                  const daysGone = get().location.state.daysVentured;
                  if (daysGone > 1 && daysGone % 18 === 0) {
                     location = getRandomBoss();
                  }
                  get().gameActions.navigateToLocation({ location });
               },

               /**
                * Attacks an enemy with a specified attack type and amount of damage.
                *
                * @param {HeroAttackType} attackType - The type of attack to perform.
                * @param {string} enemyId - The ID of the enemy to attack.
                * @return {void}
                */
               siggAttackEnemy(params: { attackType: HeroAttackType; enemyId: string }) {
                  // randomly generate the attack amount for the appropriate attack type
                  const attackTypeConfig = get().hero.state.attackTypes[params.attackType];
                  const baseAttackAmount = random(attackTypeConfig.minAttack, attackTypeConfig.maxAttack);

                  // attack the enemy with the attack amount including any upgrades
                  const bonusAttackAmount = get().hero.state.attackSkillsUnlocked.length;
                  const totalAttackAmount = baseAttackAmount + bonusAttackAmount;
                  get().enemy.actions.attackEnemy({ enemyId: params.enemyId, amount: totalAttackAmount });

                  // consume Siggs action points for this attack
                  get().hero.actions.heroicActions.useActionPointsWhenAttacking({ attackType: params.attackType });

                  // log the attack
                  get().log.actions.addLogItemSiggAttacksEnemy({
                     location: get().location.state.currentLocation,
                     attackAmount: baseAttackAmount,
                     enemy: get().enemy.state.enemies[params.enemyId],
                  });
               },

               //set the isStunned status effect on the enemy
               siggBashesEnemy(enemyId: string) {
                  get().enemy.actions.bashEnemy(enemyId);
               },

               /**
                * Attacks the hero with a random amount of damage from an enemy.
                *
                * @param {Object} params - The destructured parameter object.
                * @param {string} params.byEnemyId - The ID of the enemy attacking the hero.
                * @return {void}
                */
               enemyAttackSigg(params: { byEnemyId: string }) {
                  const enemy = get().enemy.state.enemies[params.byEnemyId];
                  const attackAmount = random(1, enemy.attackMin);
                  get().hero.actions.heroicActions.sufferDamage(attackAmount);

                  // log the attack
                  get().log.actions.addLogItemEnemyAttacksSigg({
                     location: get().location.state.currentLocation,
                     attackAmount: attackAmount,
                     enemy: enemy,
                  });
               },

               /**
                * Attacks the hero by all enemies who are not dead or stunned. recovery of stun too
                */
               allEnemiesAttackSigg() {
                  //check for stuns and recovery
                  const stunnedEnemies = get().enemy.getters.getEnemiesThatAreStunned();
                  for (const enemy of stunnedEnemies) {
                     get().enemy.actions.incrementRoundsStunned(enemy.id);
                  }
                  for (const enemy of stunnedEnemies) {
                     if (enemy && enemy.roundsStunned > 0) {
                        get().enemy.actions.recoverFromStun(enemy.id);
                     }
                  }

                  //enemies that arn't stunned or dead fight back
                  const enemiesThatAreReadyToFight = get().enemy.getters.getEnemiesThatAreReadyToFight();
                  enemiesThatAreReadyToFight.forEach((enemy) => get().gameActions.enemyAttackSigg({ byEnemyId: enemy.id }));
               },

               startNewRun() {
                  const heroActions = get().hero.actions.heroicActions;
                  heroActions.recoverHealth(1000); //overheal always sets max health
                  const locationActions = get().location.actions;
                  locationActions.setDaysVentured(0);
                  locationActions.setToStartLocation();
                  const enemyActions = get().enemy.actions;
                  enemyActions.setEnemies({ enemies: [] });
               },

               setGameHasStarted: function (): void {
                  return set((state) => {
                     state.gameHasStarted = true;
                  });
               },

               resetState() {
                  return set((state) => {
                     state.options.state = getOptionsInitialState();
                     state.enemy.state = getEnemyInitialState();
                     state.book.state = getBookInitialState();
                     state.log.state = getGameLogInitialState();
                     state.shop.state = getShopInitialState();
                     state.location.state = getLocationInitialState();
                     state.hero.state = getHeroInitialState();
                  });
               },

            },
         })),
         {
            name: 'game',
            merge: (persistedState, currentState) => {
               const rehydratedState = merge(currentState, persistedState)
               // we need to set the isBackgroundMusicTrackPlaying state to false, 
               // because a user interaction is required for audio to play in the browser
               rehydratedState.options.state.isBackgroundMusicTrackPlaying = false;
               return rehydratedState;
            },
         },
      ),
   ),
);

export const useHeroStore = () => useGameStore((state) => state.hero);
export const useLocationStore = () => useGameStore((state) => state.location);
export const useShopStore = () => useGameStore((state) => state.shop);
export const useEnemyStore = () => useGameStore((state) => state.enemy);
export const useBookStore = () => useGameStore((state) => state.book);
export const useOptionsStore = () => useGameStore((state) => state.options);
